import type { ComputedRef, Ref } from 'vue';
import { unref } from 'vue';
import type { QueryClient } from '@tanstack/vue-query';
import getAccountShipmentV2 from '@gpx-spa/queries/src/getAccountShipmentV2';
import getAccountShipmentRoutesBatch from '@gpx-spa/queries/src/getAccountShipmentRoutesBatch';
import { storeToRefs } from 'pinia';
import { useShipmentFormStore } from '@/stores/useShipmentFormStore';
import { shipmentFormStopsToDeviceApiStops } from '@/components/shipment/form/utils/shipment-form-payload';
import { computeStopsRouteFingerprint } from '@/utils/compute-stops-route-fingerprint';
import { stopPayloadInvalidatesServerRoutesFromPersistedSnapshot } from '@/components/shipment/form/utils/stop-payload-invalidates-server-routes';
import { syncFormRoutes } from '@/components/shipment/form/utils/persist/persistFormRoutes';
import {
  resolveStopWrite,
  type StopWrite,
} from '@/components/shipment/form/save/shipment-save-plan';
import type { ShipmentStopIdRow } from '@/utils/device-api-shipment-payload';
import type { ShipmentFormStopsPort } from '@/composables/shipment-form-stops-port';

function v2StopsToRouteIdRows(stopsApi: unknown): ShipmentStopIdRow[] {
  if (!Array.isArray(stopsApi)) return [];
  return [...stopsApi]
    .filter((s) => typeof (s as { id?: number }).id === 'number')
    .sort(
      (a, b) =>
        (a as { sequence: number }).sequence -
        (b as { sequence: number }).sequence,
    )
    .map((s) => ({
      id: (s as { id: number }).id,
      sequence: (s as { sequence: number }).sequence,
    }));
}

type PutStopsDeps = {
  queryClient: QueryClient;
  postStops: (args: {
    accountId: number;
    shipmentId: number;
    payload: { stops: ReturnType<typeof shipmentFormStopsToDeviceApiStops> };
  }) => Promise<unknown>;
  putStops: (args: {
    accountId: number;
    shipmentId: number;
    payload: { stops: ReturnType<typeof shipmentFormStopsToDeviceApiStops> };
    skipInvalidation?: boolean;
  }) => Promise<unknown>;
};

export function stopIdRowsFromFormStore(): ShipmentStopIdRow[] {
  const formStore = useShipmentFormStore();
  const out: ShipmentStopIdRow[] = [];
  formStore.stops.forEach((s, sequence) => {
    if (typeof s.persistedStopId === 'number') {
      out.push({ id: s.persistedStopId, sequence });
    }
  });
  return out;
}

export function haveFormStopsChanged(): boolean {
  const formStore = useShipmentFormStore();
  const { stops } = storeToRefs(formStore);
  return formStore.haveStopsChangedSincePersisted(stops.value);
}

function stopWriteFromStore(): StopWrite {
  const formStore = useShipmentFormStore();
  return resolveStopWrite({
    canEditRoute: true,
    stopsDirty: haveFormStopsChanged(),
    stops: formStore.stops,
  });
}

export async function putFormStopsOnly(
  deps: PutStopsDeps,
  options: {
    accountId: Ref<number> | ComputedRef<number>;
    shipmentId: Ref<number | null> | ComputedRef<number | null>;
    stopsPort?: Ref<ShipmentFormStopsPort | null>;
    deferInvalidation?: boolean;
    stopWrite?: StopWrite;
  },
): Promise<boolean> {
  const write = options.stopWrite ?? stopWriteFromStore();
  if (write.method === 'skip') return false;

  const sid = unref(options.shipmentId);
  if (sid == null) {
    throw new Error('Missing shipment id');
  }

  const formStore = useShipmentFormStore();
  const { stops, persistedStopsPayloadSnapshot } = storeToRefs(formStore);

  const invalidatesRoutes =
    stopPayloadInvalidatesServerRoutesFromPersistedSnapshot(
      persistedStopsPayloadSnapshot.value,
      stops.value,
    );

  const accountId = unref(options.accountId);
  const skipInvalidation = options.deferInvalidation === true;

  if (write.method === 'put') {
    await deps.putStops({
      accountId,
      shipmentId: sid,
      payload: { stops: write.stops },
      skipInvalidation,
    });
  } else {
    try {
      const posted = await deps.postStops({
        accountId,
        shipmentId: sid,
        payload: { stops: write.stops },
      });
      formStore.applyPersistedStopIdsFromPosted(v2StopsToRouteIdRows(posted));
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status !== 409) throw err;
      const detail = await getAccountShipmentV2.ensureQueryData(
        deps.queryClient,
        accountId,
        sid,
      );
      formStore.applyPersistedStopIdsFromPosted(
        v2StopsToRouteIdRows(detail.stops),
      );
      await deps.putStops({
        accountId,
        shipmentId: sid,
        payload: {
          stops: shipmentFormStopsToDeviceApiStops(formStore.stops, {
            includeIds: true,
          }),
        },
        skipInvalidation,
      });
    }
  }

  if (!options.deferInvalidation) {
    await deps.queryClient.invalidateQueries({
      queryKey: getAccountShipmentRoutesBatch.queryKey(accountId, [sid]),
    });
  }

  formStore.markStopsPayloadPersisted(stops.value);
  if (!invalidatesRoutes) {
    const fp = computeStopsRouteFingerprint(stops.value);
    if (fp != null) {
      formStore.serverHydratedRouteFingerprint = fp;
    }
  }
  return true;
}

/** @deprecated Use {@link putFormStopsOnly} plus {@link syncFormRoutes}. */
export async function persistFormStops(
  deps: PutStopsDeps & {
    postRouteSegment: (
      accountId: number,
      shipmentId: number,
      segment: unknown,
    ) => Promise<unknown>;
  },
  options: {
    accountId: Ref<number> | ComputedRef<number>;
    shipmentId: Ref<number | null> | ComputedRef<number | null>;
    stopsPort: Ref<ShipmentFormStopsPort | null>;
    postRouteSegment: (
      accountId: number,
      shipmentId: number,
      segment: unknown,
    ) => Promise<unknown>;
    replaceAllRoutesAfterStopsSave?: boolean;
  },
): Promise<boolean> {
  const changed = await putFormStopsOnly(deps, options);
  if (!changed) return false;
  await syncFormRoutes(
    {
      queryClient: deps.queryClient,
      postRouteSegment: options.postRouteSegment,
      patchRouteSegment: async () => {
        throw new Error('patchRouteSegment is required');
      },
      deleteRouteSegment: async () => {
        throw new Error('deleteRouteSegment is required');
      },
    },
    options,
    {
      mode: options.replaceAllRoutesAfterStopsSave ? 'recreate' : 'upsert',
    },
  );
  return true;
}
